<template>
  <el-card class="antenne-table-card" shadow="hover">
    <div slot="header" class="card-header">
      <div class="header-title">
        <span class="card-title">🧮 Vue par antenne</span>
      </div>
      <div class="header-filters">
        <el-radio-group
          v-model="filterType"
          size="small"
          @change="handleFilterTypeChange"
        >
          <el-radio-button label="all">Tout</el-radio-button>
          <el-radio-button label="day">Journ.</el-radio-button>
          <el-radio-button label="week">Hebdo.</el-radio-button>
          <el-radio-button label="month">Mens.</el-radio-button>
          <el-radio-button label="custom">Personnalisé</el-radio-button>
        </el-radio-group>

        <el-date-picker
          v-if="filterType === 'custom'"
          v-model="customDateRange"
          type="daterange"
          align="right"
          unlink-panels
          range-separator="à"
          start-placeholder="Début"
          end-placeholder="Fin"
          size="small"
          format="dd/MM/yyyy"
          value-format="yyyy-MM-dd"
          class="custom-date-picker"
          @change="fetchData"
        />
      </div>
    </div>

    <div class="table-container">
      <el-table
        v-loading="loading"
        :data="tableData"
        style="width: 100%"
        height="400"
        size="small"
        stripe
        border
      >
        <el-table-column prop="nom" label="Antenne" min-width="120" fixed />
        <el-table-column
          prop="active_cis_count"
          label="Contrôleurs Actifs"
          width="100"
          align="center"
        />
        <el-table-column label="Contrôles internes" align="center">
          <el-table-column
            prop="ci_c1_count"
            label="C1"
            width="60"
            align="center"
          />
          <el-table-column
            prop="ci_c2_count"
            label="C2"
            width="60"
            align="center"
          />
          <el-table-column
            prop="ci_c3_count"
            label="C3"
            width="60"
            align="center"
          />
        </el-table-column>
        <el-table-column
          prop="producteurs_count"
          label="Prod."
          width="80"
          align="center"
        />
        <el-table-column
          prop="parcelles_count"
          label="Parc."
          width="80"
          align="center"
        />
        <el-table-column
          prop="surface_totale"
          label="Surf. (ha)"
          width="100"
          align="right"
        >
          <template slot-scope="scope">
            {{ scope.row.surface_totale | numberFormat }}
          </template>
        </el-table-column>
        <el-table-column
          prop="quantite_totale"
          label="Qté Recolté (kg)"
          width="100"
          align="right"
        >
          <template slot-scope="scope">
            {{ scope.row.quantite_totale | numberFormat }}
          </template>
        </el-table-column>
        <el-table-column
          prop="formations_count"
          label="Form."
          width="70"
          align="center"
        />
      </el-table>
    </div>
  </el-card>
</template>

<script>
import DashboardAPI from '@/api/dashboard';

export default {
  name: 'AntenneTable',
  filters: {
    numberFormat(value) {
      if (!value) {
        return '0';
      }
      return new Intl.NumberFormat('fr-FR').format(value);
    },
  },
  data() {
    return {
      loading: false,
      tableData: [],
      filterType: 'all', // Default to all
      customDateRange: [],
      dateFilters: {
        start: null,
        end: null,
      },
    };
  },
  computed: {
    camp_id() {
      return this.$store.getters['camp_agricole/camp_id'] || sessionStorage.getItem('camp_id');
    },
  },
  watch: {
    customDateRange(val) {
      if (this.filterType === 'custom' && val && val.length === 2) {
        this.fetchData();
      }
    },
    camp_id: {
      handler() {
        // Recharger les données quand la campagne change
        if (this.filterType !== 'custom' || (this.customDateRange && this.customDateRange.length === 2)) {
          this.fetchData();
        }
      },
      immediate: false,
    },
  },
  created() {
    this.handleFilterTypeChange('all');
  },
  methods: {
    handleFilterTypeChange(type) {
      console.log('Type de filtre:', type);
      const now = new Date();
      const start = new Date();
      let end = new Date();

      switch (type) {
        case 'day':
          // Start and end of today
          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);
          break;
        case 'week': {
          // Start of week (Monday)
          const day = now.getDay();
          const diff = now.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
          start.setDate(diff);
          start.setHours(0, 0, 0, 0);
          end.setHours(23, 59, 59, 999);
          break;
        }
        case 'month':
          // Start of month
          start.setDate(1);
          start.setHours(0, 0, 0, 0);
          // End of month
          end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          end.setHours(23, 59, 59, 999);
          break;
        case 'all':
          // No date filtering
          this.dateFilters.start = null;
          this.dateFilters.end = null;
          break;
        case 'custom':
          // Don't fetch yet, wait for date picker
          return;
      }

      this.dateFilters.start = this.formatDate(start);
      this.dateFilters.end = this.formatDate(end);
      this.dateFilters.type = type;

      // Update custom picker visual state if needed, or just fetch
      if (type !== 'custom') {
        this.customDateRange = [this.dateFilters.start, this.dateFilters.end];
        this.fetchData();
      }
    },

    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    async fetchData() {
      this.loading = true;
      try {
        const params = {
          type: this.dateFilters.type,
          date_debut: this.dateFilters.start,
          date_fin: this.dateFilters.end,
        };

        // Ajouter la campagne_id si elle est définie
        if (this.camp_id) {
          params.campagne_id = this.camp_id;
        }

        const response = await DashboardAPI.fetchAntennesStats(params);
        this.tableData = response.data || [];
      } catch (error) {
        console.error('Erreur chargement antennes:', error);
        this.$message.error('Impossible de charger les données par antenne');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;

  .header-title {
    font-size: 16px;
    font-weight: 600;
  }

  .header-filters {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.custom-date-picker {
  width: 240px !important;
}

.table-container {
  margin-top: 10px;
}
</style>
